// frontend/src/widgets/LocationIntelligenceOverviewWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-10-08
// Updated:      2026-10-09 13:09 BST
//
// Description:  Consolidated Monitor intelligence surface for the common pinned location.
//               - Hourly weather strip for today or a selected forecast date.
//               - Leaflet street/satellite map with pinned location, Street Manager works,
//                 Environment Agency flood stations and TomTom traffic incidents.
//               - Clickable marker summaries and a colour key.
//               - Uses the same jobsPinnedLocation / jobs-pinned-location-changed contract as
//                 the existing Weather, Flood, Traffic and Works intelligence widgets.
//
// Change Summary (2026-10-08 15:24 BST):
//               - Initial implementation for Monitor dashboard.
//               - Turquoise pinned-location marker, red works, purple flood, orange traffic.
//               - OpenStreetMap street base plus Esri World Imagery satellite layer selector.
//               - Selected date controls weather and Street Manager works window; traffic and
//                 flood remain explicitly live because their canonical APIs are live intelligence.
//
// Change Summary (2026-10-09 11:08 BST):
//               - Mobile now uses natural content height and expands only its mobile grid host.
//               - Map height reduced on mobile so the complete intelligence surface is useful.
//               - Desktop markers show summaries on hover; touch devices retain tap popups.
//               - Pinned-location popup includes an OS navigation action.
//               - Hourly forecast now reuses the existing Vision animated weather SVG pack.
//               - Summary cards are interactive intelligence tabs with an expanded detail panel.
//
// Change Summary (2026-10-09 13:09 BST):
//               - Hourly weather icons are now daylight-aware using solar elevation calculated
//                 from the forecast timestamp and common pinned latitude/longitude.
//               - Clear, partly cloudy, overcast, fog and thunderstorm conditions use the existing
//                 Vision night SVG variants after sunset/before sunrise instead of daytime sun icons.
//               - No map, summary, marker interaction, navigation or mobile host sizing changes.
//
// Change Summary (2026-10-09 12:32 BST):
//               - Removed the ineffective mobile `.react-grid-item` host-height mutation. Monitor
//                 mobile uses a plain flex wrapper, not ReactGridLayout, so that effect could never
//                 resize the actual host.
//               - Mobile height ownership now correctly lives in MonitorPage; this widget remains
//                 natural-height content inside that host.
//               - Restored Intelligence Summary styling and all accepted marker interactions unchanged.
//
// Change Summary (2026-10-09 12:19 BST):
//               - Restored the original first-pass Intelligence Summary visual treatment exactly.
//               - Summary cards retain the original muted header / primary value hierarchy.
//               - Added selection behaviour without replacing the original card presentation.
//               - Explicitly inherits Vision primary text on clickable cards so global button styling
//                 cannot turn the original summary values white/invisible.
//
// Change Summary (2026-10-09 10:49 BST):
//               - Restored permanent, clearly headed Intelligence Summary beneath the map key.
//               - Summary cards remain visible while selecting expanded intelligence detail.
//               - Mobile overview (weather + map + key + summary) now owns at least one full viewport;
//                 expanded detail follows below that overview boundary.
//               - Existing desktop hover and mobile tap/navigation marker behaviour preserved unchanged.
//               - Yellow Job/Task legend entry is omitted until operational context supplies markers.
//
// Operational Notes:
//               - ATC/job yellow markers are intentionally not fabricated. The supplied Monitor
//                 source does not expose a job-specific ATC coordinate collection to this surface.
//                 The map is structured to accept that overlay when its canonical source is wired.
//               - Leaflet CSS is expected to be imported once by the application, matching
//                 LocationEditModal.jsx.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import api from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const SHARED_LOCATION_KEY = "jobsPinnedLocation";
const SHARED_LOCATION_EVENT = "jobs-pinned-location-changed";

const COLOURS = {
  pinned: "#13c8c2",
  works: "#dc2626",
  flood: "#7e22ce",
  traffic: "#f97316",
  atc: "#facc15",
};

const finite = (v) => Number.isFinite(Number(v));
const todayYmd = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const readSharedLocation = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(SHARED_LOCATION_KEY) || "null");
    if (!finite(parsed?.lat) || !finite(parsed?.lon)) return null;
    return { ...parsed, lat: Number(parsed.lat), lon: Number(parsed.lon) };
  } catch (_) {
    return null;
  }
};
const sameLocation = (a, b) => Number(a?.lat) === Number(b?.lat) && Number(a?.lon) === Number(b?.lon) && String(a?.name || "") === String(b?.name || "");
const esc = (value) => String(value ?? "").replace(/[&<>'"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" }[ch]));
const markerIcon = (colour, label = "") => L.divIcon({
  className: "vx-intelligence-map-marker",
  html: `<div style="display:flex;flex-direction:column;align-items:center;filter:drop-shadow(0 2px 2px rgba(0,0,0,.35))"><div style="width:20px;height:20px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${colour};border:2px solid white"></div>${label ? `<span style="margin-top:4px;background:rgba(17,24,39,.9);color:white;border-radius:4px;padding:2px 5px;font-size:10px;font-weight:800;white-space:nowrap">${esc(label)}</span>` : ""}</div>`,
  iconSize: [34, label ? 44 : 28], iconAnchor: [17, 24], popupAnchor: [0, -24],
});
const geometryPoint = (geometry) => {
  const coords = geometry?.coordinates;
  if (!Array.isArray(coords)) return null;
  const points = [];
  const walk = (node) => {
    if (!Array.isArray(node)) return;
    if (node.length >= 2 && finite(node[0]) && finite(node[1])) points.push([Number(node[1]), Number(node[0])]);
    else node.forEach(walk);
  };
  walk(coords);
  if (!points.length) return null;
  return [points.reduce((a, p) => a + p[0], 0) / points.length, points.reduce((a, p) => a + p[1], 0) / points.length];
};
const weatherLabel = (item) => {
  const code = Number(item?.weather_code);
  if ([0, 1].includes(code)) return "Clear";
  if ([2, 3].includes(code)) return "Cloudy";
  if ([45, 48].includes(code)) return "Fog";
  if ([51, 53, 55, 56, 57].includes(code)) return "Drizzle";
  if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return "Rain";
  if ([71, 73, 75, 77, 85, 86].includes(code)) return "Snow";
  if ([95, 96, 99].includes(code)) return "Storm";
  return "Forecast";
};

const iconsCtx = require.context(
  "../assets/weather-icons/weather-icons-master/design/fill/animation-ready",
  false,
  /\.svg$/
);
const iconSrc = (name) => {
  const safe = String(name || "not-available").trim().toLowerCase().replace(/[^a-z0-9\-]/g, "");
  try { const mod = iconsCtx(`./${safe}.svg`); return mod?.default || mod; }
  catch (_) { try { const mod = iconsCtx("./not-available.svg"); return mod?.default || mod; } catch (_) { return null; } }
};
const solarElevationDegrees = (timestamp, lat, lon) => {
  if (!timestamp || !finite(lat) || !finite(lon)) return null;
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return null;

  // NOAA-style solar-position approximation. Using the forecast timestamp itself means any
  // timezone offset supplied by the weather API is honoured by Date before the UTC calculation.
  const radians = Math.PI / 180;
  const degrees = 180 / Math.PI;
  const julianDate = parsed.getTime() / 86400000 + 2440587.5;
  const julianCentury = (julianDate - 2451545.0) / 36525.0;
  const geomMeanLong = ((280.46646 + julianCentury * (36000.76983 + julianCentury * 0.0003032)) % 360 + 360) % 360;
  const geomMeanAnom = 357.52911 + julianCentury * (35999.05029 - 0.0001537 * julianCentury);
  const eccentricity = 0.016708634 - julianCentury * (0.000042037 + 0.0000001267 * julianCentury);
  const sunEq = Math.sin(geomMeanAnom * radians) * (1.914602 - julianCentury * (0.004817 + 0.000014 * julianCentury))
    + Math.sin(2 * geomMeanAnom * radians) * (0.019993 - 0.000101 * julianCentury)
    + Math.sin(3 * geomMeanAnom * radians) * 0.000289;
  const sunTrueLong = geomMeanLong + sunEq;
  const omega = 125.04 - 1934.136 * julianCentury;
  const sunAppLong = sunTrueLong - 0.00569 - 0.00478 * Math.sin(omega * radians);
  const meanObliq = 23 + (26 + (21.448 - julianCentury * (46.815 + julianCentury * (0.00059 - julianCentury * 0.001813))) / 60) / 60;
  const obliqCorr = meanObliq + 0.00256 * Math.cos(omega * radians);
  const declination = Math.asin(Math.sin(obliqCorr * radians) * Math.sin(sunAppLong * radians));

  const y = Math.tan((obliqCorr / 2) * radians) ** 2;
  const equationOfTime = 4 * degrees * (
    y * Math.sin(2 * geomMeanLong * radians)
    - 2 * eccentricity * Math.sin(geomMeanAnom * radians)
    + 4 * eccentricity * y * Math.sin(geomMeanAnom * radians) * Math.cos(2 * geomMeanLong * radians)
    - 0.5 * y * y * Math.sin(4 * geomMeanLong * radians)
    - 1.25 * eccentricity * eccentricity * Math.sin(2 * geomMeanAnom * radians)
  );

  const utcMinutes = parsed.getUTCHours() * 60 + parsed.getUTCMinutes() + parsed.getUTCSeconds() / 60;
  let trueSolarMinutes = (utcMinutes + equationOfTime + 4 * Number(lon)) % 1440;
  if (trueSolarMinutes < 0) trueSolarMinutes += 1440;
  const hourAngle = (trueSolarMinutes / 4 < 0 ? trueSolarMinutes / 4 + 180 : trueSolarMinutes / 4 - 180) * radians;
  const latitude = Number(lat) * radians;
  const cosZenith = Math.sin(latitude) * Math.sin(declination) + Math.cos(latitude) * Math.cos(declination) * Math.cos(hourAngle);
  const zenith = Math.acos(Math.max(-1, Math.min(1, cosZenith))) * degrees;
  return 90 - zenith;
};

const isForecastDaylight = (item, lat, lon) => {
  const elevation = solarElevationDegrees(item?.time, lat, lon);
  // -0.833° approximates the apparent upper limb of the sun at sunrise/sunset.
  return elevation == null ? true : elevation >= -0.833;
};

const weatherIconName = (item, daylight = true) => {
  const symbol = String(item?.symbol || item?.wx_symbol || item?.weather_symbol || item?.weatherSymbol || item?.icon || "").toLowerCase();
  const code = Number(item?.weather_code ?? item?.code);
  const phase = daylight ? "day" : "night";
  if (symbol.includes("clear") || symbol.includes("sun")) return `clear-${phase}`;
  if (symbol.includes("partly") || symbol.includes("broken")) return `partly-cloudy-${phase}`;
  if (symbol.includes("overcast") || symbol.includes("cloud")) return `overcast-${phase}`;
  if (symbol.includes("fog") || symbol.includes("mist") || symbol.includes("haze")) return `fog-${phase}`;
  if (symbol.includes("drizzle")) return "drizzle";
  if (symbol.includes("sleet")) return "sleet";
  if (symbol.includes("snow")) return "snow";
  if (symbol.includes("hail")) return "hail";
  if (symbol.includes("thunder") || symbol.includes("storm")) return `thunderstorms-${phase}`;
  if (symbol.includes("rain") || symbol.includes("shower")) return "rain";
  if (Number.isFinite(code)) {
    if (code === 0) return `clear-${phase}`;
    if ([1,2,3].includes(code)) return `partly-cloudy-${phase}`;
    if ([4,5].includes(code)) return `overcast-${phase}`;
    if ([6,7,8,45,48].includes(code)) return `fog-${phase}`;
    if ([9,10,51,53,55,56,57].includes(code)) return "drizzle";
    if ([11,12,13,14,61,63,65,66,67,80,81,82].includes(code)) return "rain";
    if ([15,16].includes(code)) return "sleet";
    if ([17,18].includes(code)) return "hail";
    if ([19,20,21,22,71,73,75,77,85,86].includes(code)) return "snow";
    if ([23,24,25,26,27,28,95,96,99].includes(code)) return `thunderstorms-${phase}`;
  }
  return "not-available";
};
const navigationHref = (lat, lon, label) => {
  const encoded = encodeURIComponent(label || "Destination");
  const isiOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent || "");
  return isiOS ? `maps://?daddr=${lat},${lon}&q=${encoded}` : `geo:${lat},${lon}?q=${lat},${lon}(${encoded})`;
};

export default function LocationIntelligenceOverviewWidget() {
  const { theme } = useTheme();
  const rootRef = useRef(null);
  const contentRef = useRef(null);
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const [loc, setLoc] = useState(() => readSharedLocation());
  const [selectedDate, setSelectedDate] = useState(todayYmd());
  const [weather, setWeather] = useState(null);
  const [works, setWorks] = useState(null);
  const [flood, setFlood] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [activeSummary, setActiveSummary] = useState("Weather");
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches);

  const hasLocation = finite(loc?.lat) && finite(loc?.lon);
  const pinnedName = String(loc?.name || "Pinned Location").trim() || "Pinned Location";
  const panel = theme?.card_background || theme?.background_secondary || "#ffffff";
  const text = theme?.text_primary || "#111827";
  const muted = theme?.text_secondary || "#6b7280";
  const border = theme?.border || "#d1d5db";

  useEffect(() => {
    const apply = (candidate) => {
      if (!finite(candidate?.lat) || !finite(candidate?.lon)) return;
      const next = { ...candidate, lat: Number(candidate.lat), lon: Number(candidate.lon) };
      setLoc((prev) => (sameLocation(prev, next) ? prev : next));
    };
    apply(readSharedLocation());
    const onStorage = (e) => { if (e.key === SHARED_LOCATION_KEY) apply(readSharedLocation()); };
    const onShared = (e) => apply(e?.detail);
    window.addEventListener("storage", onStorage);
    window.addEventListener(SHARED_LOCATION_EVENT, onShared);
    return () => { window.removeEventListener("storage", onStorage); window.removeEventListener(SHARED_LOCATION_EVENT, onShared); };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, []);

  const fetchAll = useCallback(async () => {
    if (!hasLocation) return;
    setBusy(true); setError("");
    const start = `${selectedDate}T00:00:00`;
    const end = `${selectedDate}T23:59:59`;
    const requests = await Promise.allSettled([
      api.get("/weather/spot", { params: { lat: loc.lat, lon: loc.lon, period: "hourly", start_date: selectedDate, days: 24 } }),
      api.get("/works-intelligence", { params: { lat: loc.lat, lon: loc.lon, radius_m: 5000, limit: 50, window_start: start, window_end: end } }),
      api.get("/flood/intelligence", { params: { lat: loc.lat, lon: loc.lon, persist: true } }),
      api.get("/traffic/intelligence", { params: { lat: loc.lat, lon: loc.lon, persist: true, include_raw: false } }),
    ]);
    const setters = [setWeather, setWorks, setFlood, setTraffic];
    requests.forEach((r, i) => setters[i](r.status === "fulfilled" ? r.value?.data || null : null));
    const failures = requests.filter((r) => r.status === "rejected");
    if (failures.length) setError(`${failures.length} intelligence source${failures.length === 1 ? "" : "s"} could not be loaded.`);
    setBusy(false);
  }, [hasLocation, loc?.lat, loc?.lon, selectedDate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const hourly = useMemo(() => (Array.isArray(weather?.series) ? weather.series.filter((x) => String(x?.time || "").slice(0, 10) === selectedDate) : []), [weather, selectedDate]);
  const disruptions = Array.isArray(works?.disruptions) ? works.disruptions : [];
  const stations = Array.isArray(flood?.stations) ? flood.stations : [];
  const incidents = Array.isArray(traffic?.incidents) ? traffic.incidents : [];

  useEffect(() => {
    if (!mapNodeRef.current || !hasLocation) return undefined;
    if (!mapRef.current) {
      const map = L.map(mapNodeRef.current, { zoomControl: true }).setView([loc.lat, loc.lon], 14);
      const street = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "&copy; OpenStreetMap contributors" });
      const satellite = L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { maxZoom: 19, attribution: "Tiles &copy; Esri" });
      street.addTo(map);
      L.control.layers({ "Street Map": street, "Satellite": satellite }, null, { position: "topright" }).addTo(map);
      overlayRef.current = L.layerGroup().addTo(map);
      mapRef.current = map;
      setTimeout(() => map.invalidateSize(), 0);
    }
    const map = mapRef.current;
    const layer = overlayRef.current;
    layer.clearLayers();
    const bounds = [];
    const touchMode = typeof window !== "undefined" && window.matchMedia("(hover: none), (pointer: coarse)").matches;
    const add = (lat, lon, colour, popup, label = "") => {
      if (!finite(lat) || !finite(lon)) return;
      const point = [Number(lat), Number(lon)];
      const marker = L.marker(point, { icon: markerIcon(colour, label) }).bindPopup(popup, { maxWidth: 340, closeButton: touchMode });
      if (!touchMode) { marker.on("mouseover", () => marker.openPopup()); marker.on("mouseout", () => marker.closePopup()); }
      marker.addTo(layer);
      bounds.push(point);
    };
    const nav = navigationHref(loc.lat, loc.lon, pinnedName);
    add(loc.lat, loc.lon, COLOURS.pinned, `<strong>${esc(pinnedName)}</strong><br/>${Number(loc.lat).toFixed(6)}, ${Number(loc.lon).toFixed(6)}<br/><a href="${esc(nav)}" style="display:inline-block;margin-top:8px;font-weight:800">Navigate to location</a>`, pinnedName);
    disruptions.forEach((d) => add(d.lat, d.lon, COLOURS.works, `<strong>${esc(d.street_name || d.location_text || "Road works")}</strong><br/>${esc(d.event_type || d.source_topic || "Street Manager works")}<br/>${esc(d.traffic_management_type || "")}<br/>${esc(d.event_details || "")}<br/>${finite(d.distance_m) ? `${Math.round(Number(d.distance_m))} m from pin` : ""}`));
    stations.forEach((s) => add(s.lat, s.lon, COLOURS.flood, `<strong>${esc(s.label || "Flood station")}</strong><br/>Risk: ${esc(flood?.risk_level || "unknown")}<br/>Level: ${esc(s.latest_value ?? "n/a")} ${esc(s.unit || "")}<br/>Trend: ${esc(s.trend || "unknown")}<br/>${finite(s.distance_km) ? `${Number(s.distance_km).toFixed(2)} km from pin` : ""}`));
    incidents.forEach((i) => { const p = geometryPoint(i.geometry); if (p) add(p[0], p[1], COLOURS.traffic, `<strong>${esc((i.event_descriptions || []).join(" · ") || "Traffic incident")}</strong><br/>${esc(i.from || "")} ${i.to ? `→ ${esc(i.to)}` : ""}<br/>${esc(i.delay_display_text || "")}<br/>${esc((i.road_numbers || []).join(", "))}`); });
    if (bounds.length > 1) map.fitBounds(bounds, { padding: [34, 34], maxZoom: 15 }); else map.setView([loc.lat, loc.lon], 14);
    setTimeout(() => map.invalidateSize(), 0);
    return undefined;
  }, [hasLocation, loc?.lat, loc?.lon, pinnedName, disruptions, stations, incidents, flood?.risk_level]);

  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; overlayRef.current = null; } }, []);

  const summaryCards = [
    ["Weather", hourly.length ? `${hourly.length} hourly periods` : "No forecast", COLOURS.pinned],
    ["Traffic", `${incidents.length} surfaced incident${incidents.length === 1 ? "" : "s"}`, COLOURS.traffic],
    ["Road Works", `${disruptions.length} relevant work${disruptions.length === 1 ? "" : "s"}`, COLOURS.works],
    ["Flood", flood?.risk_level ? `${String(flood.risk_level).toUpperCase()} risk` : "No result", COLOURS.flood],
  ];

  const detailPanel = (() => {
    if (activeSummary === "Traffic") return incidents.length ? incidents.map((i, idx) => <div key={i.id || idx} className="rounded-lg border p-3" style={{ borderColor:border }}><div className="font-bold">{(i.event_descriptions || []).join(" · ") || "Traffic incident"}</div><div className="text-sm" style={{color:muted}}>{i.from || ""}{i.to ? ` → ${i.to}` : ""}{i.delay_display_text ? ` · ${i.delay_display_text}` : ""}</div><div className="text-xs" style={{color:muted}}>{(i.road_numbers || []).join(", ")}</div></div>) : <div className="text-sm" style={{color:muted}}>No surfaced traffic incidents.</div>;
    if (activeSummary === "Road Works") return disruptions.length ? disruptions.map((d, idx) => <div key={d.id || idx} className="rounded-lg border p-3" style={{ borderColor:border }}><div className="font-bold">{d.street_name || d.location_text || "Road works"}</div><div className="text-sm" style={{color:muted}}>{d.event_type || d.source_topic || "Street Manager works"}{d.traffic_management_type ? ` · ${d.traffic_management_type}` : ""}</div>{d.event_details ? <div className="mt-1 text-sm">{d.event_details}</div> : null}</div>) : <div className="text-sm" style={{color:muted}}>No relevant road works for the selected date.</div>;
    if (activeSummary === "Flood") return <div className="space-y-2"><div className="font-bold">Overall risk: {flood?.risk_level ? String(flood.risk_level).toUpperCase() : "No result"}</div>{stations.length ? stations.map((st, idx) => <div key={st.id || idx} className="rounded-lg border p-3" style={{borderColor:border}}><div className="font-bold">{st.label || "Flood station"}</div><div className="text-sm" style={{color:muted}}>Level: {st.latest_value ?? "n/a"} {st.unit || ""} · Trend: {st.trend || "unknown"}{finite(st.distance_km) ? ` · ${Number(st.distance_km).toFixed(2)} km from pin` : ""}</div></div>) : <div className="text-sm" style={{color:muted}}>No nearby flood stations surfaced.</div>}</div>;
    return hourly.length ? <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">{hourly.map((h, idx) => <div key={`${h.time}-${idx}`} className="flex items-center gap-3 rounded-lg border p-3" style={{borderColor:border}}><img src={iconSrc(weatherIconName(h, isForecastDaylight(h, loc.lat, loc.lon)))} alt={weatherLabel(h)} className="h-10 w-10"/><div><div className="font-bold">{String(h.time || "").slice(11,16) || "--:--"} · {finite(h.temp_c) ? `${Math.round(Number(h.temp_c))}°C` : "—"}</div><div className="text-sm" style={{color:muted}}>{weatherLabel(h)}{finite(h.precip_prob_pct) ? ` · ${h.precip_prob_pct}% rain` : ""}</div></div></div>)}</div> : <div className="text-sm" style={{color:muted}}>No hourly forecast returned for this date.</div>;
  })();

  return (
    <div ref={rootRef} className="w-full min-w-0 p-3 md:h-full md:min-h-0 md:overflow-auto" style={{ color: text }}>
      <style>{`.vx-intelligence-map-marker{background:transparent!important;border:none!important}.leaflet-popup-content{line-height:1.45}`}</style>
      <div ref={contentRef} className="flex min-w-0 flex-col gap-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0"><div className="text-lg font-bold">Location Intelligence Overview</div><div className="truncate text-sm" style={{ color: muted }}>Pinned: {pinnedName}</div></div>
          <div className="flex items-end gap-2"><label className="text-xs font-semibold">Forecast / works date<input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value || todayYmd())} className="mt-1 block min-h-10 rounded border px-2 py-1" style={{ background: panel, color: text, borderColor: border }} /></label><button type="button" onClick={fetchAll} className="min-h-10 rounded px-3 py-2 text-sm font-bold" style={{ background: theme?.button_action?.normal || "#111827", color: "#fff" }}>{busy ? "Loading…" : "Refresh"}</button></div>
        </div>
        {error ? <div className="rounded border px-3 py-2 text-sm" style={{ borderColor: "#f59e0b", background: "rgba(245,158,11,.10)" }}>{error}</div> : null}
        {!hasLocation ? <div className="rounded border p-4 text-sm" style={{ borderColor: border }}>Choose a common pinned location in one of the Monitor intelligence widgets first.</div> : <>
          <div className="flex min-w-0 flex-col gap-3" style={isMobile ? { minHeight: "100dvh" } : undefined}>
            <section className="rounded-xl border p-3" style={{ background: panel, borderColor: border }}>
              <div className="mb-2 flex items-center justify-between"><strong>Hourly Weather — {selectedDate}</strong><span className="text-xs" style={{ color: muted }}>{weather?.location?.name || pinnedName}</span></div>
              <div className="flex gap-2 overflow-x-auto pb-1">{hourly.length ? hourly.map((h, idx) => <div key={`${h.time}-${idx}`} className="min-w-[96px] rounded-lg border p-2 text-center" style={{ borderColor: border }}><div className="text-xs font-bold">{String(h.time || "").slice(11,16) || "--:--"}</div><img src={iconSrc(weatherIconName(h, isForecastDaylight(h, loc.lat, loc.lon)))} alt={weatherLabel(h)} className="mx-auto my-1 h-10 w-10"/><div className="text-lg font-bold">{finite(h.temp_c) ? `${Math.round(Number(h.temp_c))}°` : "—"}</div><div className="text-[11px]" style={{ color: muted }}>{weatherLabel(h)}</div><div className="text-[11px]" style={{ color: muted }}>{finite(h.precip_prob_pct) ? `${h.precip_prob_pct}% rain` : ""}</div></div>) : <div className="text-sm" style={{ color: muted }}>No hourly forecast returned for this date.</div>}</div>
            </section>
            <section className="overflow-hidden rounded-xl border" style={{ borderColor: border, background: panel }}><div ref={mapNodeRef} style={{ height: isMobile ? "300px" : "440px", width: "100%", minHeight: isMobile ? "260px" : "320px" }} /></section>
            <div className="flex flex-wrap gap-x-5 gap-y-2 rounded-xl border p-3 text-xs font-semibold" style={{ borderColor: border, background: panel }}>{[["Pinned Location",COLOURS.pinned],["Road Works",COLOURS.works],["Flood Intelligence",COLOURS.flood],["TomTom Traffic",COLOURS.traffic]].map(([label,c]) => <span key={label} className="inline-flex items-center gap-2"><span className="inline-block h-3 w-3 rounded-full" style={{ background:c }} />{label}</span>)}</div>
            <section>
              <div className="mb-2 text-sm font-bold uppercase" style={{ color: text }}>Intelligence Summary</div>
              <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">{summaryCards.map(([label,value,c]) => <button type="button" key={label} onClick={() => setActiveSummary(label)} className="rounded-xl border p-3 text-left" aria-pressed={activeSummary === label} style={{ color:text, borderColor:activeSummary === label ? c : border, background:panel, borderLeft:`4px solid ${c}`, boxShadow:activeSummary === label ? `0 0 0 1px ${c}` : "none" }}><div className="text-xs font-bold uppercase" style={{ color:muted }}>{label}</div><div className="mt-1 font-semibold" style={{ color:text }}>{value}</div></button>)}</div>
            </section>
          </div>
          <section className="rounded-xl border p-3" style={{borderColor:border,background:panel}}><div className="mb-3 text-sm font-bold uppercase" style={{color:muted}}>{activeSummary} Intelligence</div><div className="space-y-2">{detailPanel}</div></section>
          <div className="text-xs" style={{ color: muted }}>Traffic and flood are live intelligence. The selected date controls the hourly weather forecast and Street Manager works window. Satellite imagery is available from the map layer control. Job/Task location markers and their legend entry appear only when an operational Job/Task context is supplied in a future surface.</div>
        </>}
      </div>
    </div>
  );
}
